import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { pollEvery } from '../realtime/live';

/* ------------------------------------------------------------------- types */
export interface PoolSettings {
  opensAt: string;
  closesAt: string;
  slotMinutes: number;
  capacity: number;
  slots: string[];
}
export interface PoolProduct {
  id: string;
  name: string;
  price: number;
  active: boolean;
}
export interface PoolSlot {
  slot: string;
  booked: number;
  remaining: number;
}
export interface PoolBooking {
  id: string;
  reference: string;
  date: string;
  slot: string;
  people: number;
  productName: string;
  unitPrice: number;
  total: number;
  customerName: string;
  phone: string | null;
  bookingId: string | null;
  status: 'BOOKED' | 'ARRIVED' | 'CANCELLED' | 'NO_SHOW';
  notes: string | null;
  paid: boolean;
  paidWith: string | null;
  refunded: boolean;
}

export interface Vehicle {
  id: string;
  name: string;
  plate: string | null;
  seats: number;
  active: boolean;
}
export interface TripType {
  id: string;
  name: string;
  price: number;
  durationMinutes: number;
  active: boolean;
}
export interface Driver {
  id: string;
  name: string;
}
export type TripStatus = 'REQUESTED' | 'CONFIRMED' | 'DISPATCHED' | 'COMPLETED' | 'CANCELLED';
export interface TransportRequest {
  id: string;
  reference: string;
  tripName: string;
  price: number;
  pickup: string;
  dropoff: string;
  pickupAt: string;
  durationMinutes: number;
  passengers: number;
  customerName: string;
  phone: string | null;
  bookingId: string | null;
  status: TripStatus;
  vehicle: { id: string; name: string; seats: number } | null;
  driver: { id: string; name: string } | null;
  notes: string | null;
  cancelReason: string | null;
  paid: boolean;
  paidWith: string | null;
  refunded: boolean;
}
/** What a driver sees: where and when, never what the guest pays. */
export type DriverTrip = Omit<TransportRequest, 'price' | 'paid' | 'paidWith' | 'refunded'>;

/* ----------------------------------------------------------------- queries */
export const usePoolSettings = () => useQuery({ queryKey: ['pool', 'settings'], queryFn: () => api<PoolSettings>('/pool/settings') });
export const usePoolProducts = (all = false) => useQuery({ queryKey: ['pool', 'products', all], queryFn: () => api<{ products: PoolProduct[] }>(`/pool/products${all ? '?all=1' : ''}`).then((r) => r.products) });
export const usePoolAvailability = (date: string) => useQuery({ queryKey: ['pool', 'availability', date], refetchInterval: pollEvery(20_000), queryFn: () => api<{ capacity: number; slots: PoolSlot[] }>(`/pool/availability?date=${date}`) });
export const usePoolBookings = (date: string) => useQuery({ queryKey: ['pool', 'bookings', date], refetchInterval: pollEvery(20_000), queryFn: () => api<{ bookings: PoolBooking[] }>(`/pool/bookings?date=${date}`).then((r) => r.bookings) });

export const useVehicles = (all = false) => useQuery({ queryKey: ['transport', 'vehicles', all], queryFn: () => api<{ vehicles: Vehicle[] }>(`/transport/vehicles${all ? '?all=1' : ''}`).then((r) => r.vehicles) });
export const useTripTypes = (all = false) => useQuery({ queryKey: ['transport', 'trip-types', all], queryFn: () => api<{ tripTypes: TripType[] }>(`/transport/trip-types${all ? '?all=1' : ''}`).then((r) => r.tripTypes) });
export const useDrivers = (enabled = true) => useQuery({ queryKey: ['transport', 'drivers'], enabled, queryFn: () => api<{ drivers: Driver[] }>('/transport/drivers').then((r) => r.drivers) });
export const useTransportRequests = (query: string) => useQuery({ queryKey: ['transport', 'requests', query], refetchInterval: pollEvery(30_000), queryFn: () => api<{ requests: TransportRequest[] }>(`/transport/requests${query}`).then((r) => r.requests) });
export const useMyTrips = () => useQuery({ queryKey: ['transport', 'mine'], refetchInterval: pollEvery(30_000), queryFn: () => api<{ trips: DriverTrip[] }>('/transport/my-trips').then((r) => r.trips) });

/** Pool and transport changes can touch tills, folios and today's totals. */
export function useSvcMutation<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      for (const k of ['pool', 'transport', 'laundry', 'rs', 'daily', 'bookings']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}
